import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Button, Collapse, Input, Popconfirm, Tooltip, message } from 'antd'
import dayjs from 'dayjs'
import {
    CheckOutlined,
    ClockCircleOutlined,
    CloseOutlined,
    CopyOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    GlobalOutlined,
    LoadingOutlined,
    LockFilled,
    PauseOutlined,
    ReloadOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    SafetyCertificateOutlined,
    StarFilled,
    StarOutlined,
    SyncOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
} from '@ant-design/icons'
import { Titles } from '../../../layouts/Titles'
import i from '../../../translations/i'
import { storeLocal } from '../../../helpers/stores'
import controller, { DnsRecord, DomainItem } from './domain.controller'
import './domain.less'

// ---------- Помощники ----------

// Как сервер поймёт введённое: без схемы, пути, порта и www. Только для подсказки под полем — нормализует сервер
const previewDomain = (raw: string) => raw
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .split(/[/?#]/)[0]
    .replace(/:\d+$/, '')
    .replace(/\.$/, '')
    .replace(/^www\./, '')

const formatDate = (iso: string | null) => (iso ? dayjs(iso).format('DD.MM.YYYY') : '')

const formatCountdown = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

// Время «сейчас», тикающее раз в секунду: для обратного отсчёта до следующей проверки
const useNow = (active: boolean) => {
    const [now, setNow] = useState(Date.now())

    useEffect(() => {
        if (!active) return
        const timer = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(timer)
    }, [active])

    return now
}

type StepState = 'done' | 'current' | 'next' | 'wait' | 'error' | 'paused'

const STEPS = ['dns', 'ssl', 'ready'] as const

const stepStates = (domain: DomainItem): StepState[] => {
    switch (domain.status) {
        case 'pending_dns': return ['current', 'wait', 'wait']
        case 'dns_timeout': return ['paused', 'wait', 'wait']
        case 'dns_ok': return ['done', 'next', 'wait']
        case 'issuing': return ['done', 'current', 'wait']
        case 'error': return ['done', 'error', 'wait']
        default: return ['done', 'done', 'done']
    }
}

const stepIcon = (state: StepState, index: number) => {
    switch (state) {
        case 'done': return <CheckOutlined />
        case 'current': return index === 0 ? <SyncOutlined spin /> : <LoadingOutlined />
        case 'error': return <CloseOutlined />
        case 'next': return <SafetyCertificateOutlined />
        case 'paused': return <PauseOutlined />
        default: return index + 1
    }
}

// ---------- Копирование одним кликом ----------

const CopyValue = ({ value, label }: { value: string, label?: string }) => {
    const [copied, setCopied] = useState(false)

    const copy = () => {
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(true)
            message.success(i.t('list:copied'))
            setTimeout(() => setCopied(false), 1600)
        })
    }

    return (
        <Tooltip title={copied ? i.t('domain:records.copied') : i.t('domain:records.copy')}>
            <button type="button" className={`cd-copy${copied ? ' cd-copy--done' : ''}`} onClick={copy} aria-label={label ?? value}>
                <span className="cd-copy__text">{value}</span>
                <span className="cd-copy__icon">{copied ? <CheckOutlined /> : <CopyOutlined />}</span>
            </button>
        </Tooltip>
    )
}

// ---------- Окно браузера: показывает адрес, который вводит человек ----------

const BrowserMock = ({ host }: { host: string }) => (
    <div className="cd-browser" aria-hidden>
        <div className="cd-browser__bar">
            <span className="cd-browser__dots"><i /><i /><i /></span>
            <span className="cd-browser__url">
                <LockFilled />
                <span className="cd-browser__host">https://{host}</span>
            </span>
        </div>
        <div className="cd-browser__body">
            <span className="cd-sk cd-sk--hero" />
            <span className="cd-browser__row"><span className="cd-sk" /><span className="cd-sk" /><span className="cd-sk" /></span>
            <span className="cd-sk cd-sk--line" />
            <span className="cd-sk cd-sk--line cd-sk--short" />
        </div>
    </div>
)

// ---------- Шапка с вводом домена ----------

const Hero = observer(() => {
    const [value, setValue] = useState('')
    const preview = previewDomain(value)
    const error = controller.addError
    // Форма видна всегда, чтобы было понятно, где добавить домен; неактивна, пока функция не включена или исчерпан лимит
    const formActive = controller.settings.enabled && controller.canAdd

    const submit = async () => {
        if (!value.trim() || controller.adding) return
        if (await controller.add(value)) setValue('')
    }

    return (
        <section className="cd-hero">
            <div className="cd-hero__main">
                <h2 className="cd-hero__title">{i.t('domain:hero.title')}</h2>
                <p className="cd-hero__text">{i.t('domain:hero.text')}</p>

                {!controller.settings.enabled && (
                    <div className="cd-note cd-note--info"><ExclamationCircleFilled />{i.t('domain:disabled')}</div>
                )}

                <div className="cd-add">
                    <Input
                        size="large"
                        className="cd-add__input"
                        prefix={<span className="cd-add__scheme">https://</span>}
                        placeholder={i.t('domain:hero.placeholder')}
                        value={value}
                        status={error ? 'error' : undefined}
                        disabled={!formActive}
                        onChange={e => { setValue(e.target.value); controller.addError = null }}
                        onPressEnter={submit}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        inputMode="url"
                    />
                    <Button type="primary" size="large" icon={<PlusOutlined />} loading={controller.adding} disabled={!formActive || !preview} onClick={submit}>
                        {i.t('domain:hero.connect')}
                    </Button>
                </div>

                {formActive && (error
                    ? <div className="cd-add__error"><CloseCircleFilled />{i.t(`domain:addErrors.${error}`, { defaultValue: i.t('domain:addErrors.unknown'), max: controller.settings.max })}</div>
                    : <div className="cd-add__hint">{preview && preview !== value.trim().toLowerCase() ? i.t('domain:hero.preview', { domain: preview }) : i.t('domain:hero.example')}</div>)}

                {controller.settings.enabled && !controller.canAdd && (
                    <div className="cd-note cd-note--info"><ExclamationCircleFilled />{i.t('domain:limit', { count: controller.items.length, max: controller.settings.max })}</div>
                )}
            </div>

            <div className="cd-hero__art">
                <BrowserMock host={preview || i.t('domain:hero.placeholder')} />
            </div>
        </section>
    )
})

// Образец для корневого домена: показываем сразу, до добавления домена, чтобы не пришлось гадать, что прописывать
const SAMPLE_IP = '203.0.113.10'

const HowItWorks = observer(() => (
    <>
        <section className="cd-how">
            <h3 className="cd-how__title">{i.t('domain:how.title')}</h3>
            <div className="cd-how__grid">
                {[1, 2, 3].map(n => (
                    <div className="cd-how__item" key={n}>
                        <span className="cd-how__num">{n}</span>
                        <div>
                            <div className="cd-how__name">{i.t(`domain:how.s${n}.title`)}</div>
                            <div className="cd-how__text">{i.t(`domain:how.s${n}.text`)}</div>
                        </div>
                    </div>
                ))}
            </div>
        </section>

        <HowTo sample record={{ type: 'A', host: '@', value: controller.settings.ipv4 || SAMPLE_IP, required: true, ok: null }} />
    </>
))

// ---------- Шаги ----------

const Steps = ({ domain }: { domain: DomainItem }) => {
    const states = stepStates(domain)

    return (
        <ol className="cd-steps">
            {STEPS.map((key, index) => (
                <li key={key} className={`cd-step cd-step--${states[index]}`}>
                    <span className="cd-step__dot">{stepIcon(states[index], index)}</span>
                    <span className="cd-step__label">{i.t(`domain:steps.${key}`)}</span>
                </li>
            ))}
        </ol>
    )
}

// ---------- DNS-записи ----------

const RecordStatus = ({ record, domain }: { record: DnsRecord, domain: DomainItem }) => {
    if (record.ok === null) {
        return <span className="cd-rec__state cd-rec__state--muted">{i.t('domain:records.checking')}</span>
    }

    if (record.ok) {
        return <span className="cd-rec__state cd-rec__state--ok"><CheckCircleFilled />{i.t('domain:records.ok')}</span>
    }

    if (!record.required) {
        return <span className="cd-rec__state cd-rec__state--muted">{i.t('domain:records.optionalOff')}</span>
    }

    const found = [...domain.dns.found.a, ...domain.dns.found.aaaa]

    return (
        <span className="cd-rec__state cd-rec__state--bad">
            <CloseCircleFilled />
            {found.length ? i.t('domain:records.now', { value: found.join(', ') }) : i.t('domain:records.missing')}
        </span>
    )
}

const Record = ({ record, domain }: { record: DnsRecord, domain: DomainItem }) => (
    <div className={`cd-rec${record.required ? '' : ' cd-rec--optional'}`}>
        <div className="cd-rec__cell" data-label={i.t('domain:records.type')}>
            <span className="cd-rec__type">{record.type}</span>
        </div>
        <div className="cd-rec__cell" data-label={i.t('domain:records.host')}>
            <CopyValue value={record.host} />
        </div>
        <div className="cd-rec__cell cd-rec__cell--value" data-label={i.t('domain:records.value')}>
            <CopyValue value={record.value} />
            {record.alternative && controller.settings.ipv4 &&
                <span className="cd-rec__or">{i.t('domain:records.orA', { ip: controller.settings.ipv4 })}</span>
            }
            {!record.required && <span className="cd-rec__or">{i.t('domain:records.wwwHint', { domain: domain.unicode })}</span>}
        </div>
        <div className="cd-rec__cell" data-label={i.t('domain:records.status')}>
            <RecordStatus record={record} domain={domain} />
        </div>
    </div>
)

// Микро-инструкция: как добавить запись у регистратора. Образец формы заполнен значениями именно этого домена
const HowTo = ({ record, sample = false }: { record: DnsRecord | undefined, sample?: boolean }) => {
    const main = record
    if (!main) return null

    const apex = main.host === '@'

    return (
        <section className="cd-howto">
            <div className="cd-howto__head">
                <span className="cd-howto__icon"><QuestionCircleOutlined /></span>
                <div>
                    <div className="cd-howto__title">{i.t('domain:registrar.title')}</div>
                    <div className="cd-howto__lead">{i.t(main.type === 'A' ? 'domain:registrar.explainA' : 'domain:registrar.explainCname')}</div>
                </div>
            </div>

            <div className="cd-howto__body">
                <ol className="cd-howto__steps">
                    {[1, 2, 3, 4].map(n => (
                        <li key={n}>
                            <span className="cd-howto__num">{n}</span>
                            <span className="cd-howto__step">
                                <b>{i.t(`domain:registrar.s${n}.title`)}</b>
                                <span>{i.t(`domain:registrar.s${n}.text`)}</span>
                            </span>
                        </li>
                    ))}
                </ol>

                <div className="cd-howto__form" role="group" aria-label={i.t('domain:registrar.formTitle')}>
                    <div className="cd-howto__formtitle">{i.t('domain:registrar.formTitle')}</div>
                    <div className="cd-howto__field">
                        <span className="cd-howto__label">{i.t('domain:registrar.type')}</span>
                        <span className="cd-rec__type">{main.type}</span>
                    </div>
                    <div className="cd-howto__field">
                        <span className="cd-howto__label">{i.t('domain:registrar.name')}</span>
                        <div className="cd-howto__value">
                            <CopyValue value={main.host} />
                            <span className="cd-howto__hint">{apex ? i.t('domain:registrar.nameApex') : i.t('domain:registrar.nameSub', { host: main.host })}</span>
                        </div>
                    </div>
                    <div className="cd-howto__field">
                        <span className="cd-howto__label">{i.t('domain:registrar.value')}</span>
                        <CopyValue value={main.value} />
                    </div>
                    <div className="cd-howto__field">
                        <span className="cd-howto__label">TTL</span>
                        <div className="cd-howto__value">
                            <span className="cd-howto__mono">300</span>
                            <span className="cd-howto__hint">{i.t('domain:registrar.ttl')}</span>
                        </div>
                    </div>
                    {sample && <div className="cd-howto__sample">{i.t('domain:registrar.sampleNote')}</div>}
                </div>
            </div>
        </section>
    )
}

const DnsPanel = observer(({ domain }: { domain: DomainItem }) => {
    const paused = domain.status === 'dns_timeout'
    const now = useNow(!paused && domain.status === 'pending_dns')
    const checking = !!controller.checking[domain.id]

    const next = domain.dns.next_check_at ? Math.max(0, Math.round((new Date(domain.dns.next_check_at).getTime() - now) / 1000)) : null
    const problem = domain.dns.problem
    const found = [...domain.dns.found.a, ...domain.dns.found.aaaa].join(', ')

    return (
        <div className="cd-dns">
            <div className="cd-dns__title">{i.t('domain:records.title')}</div>

            <div className="cd-recs">
                <div className="cd-rec cd-rec--head">
                    <div>{i.t('domain:records.type')}</div>
                    <div>{i.t('domain:records.host')}</div>
                    <div>{i.t('domain:records.value')}</div>
                    <div>{i.t('domain:records.status')}</div>
                </div>
                {domain.dns.records.map((record, index) => <Record key={`${record.type}_${record.host}_${index}`} record={record} domain={domain} />)}
            </div>

            {problem && (
                <div className="cd-note cd-note--warning">
                    <ExclamationCircleFilled />
                    <span>{i.t(`domain:problems.${problem}`, { found, ip: controller.settings.ipv4, defaultValue: '' })}</span>
                </div>
            )}

            <HowTo record={domain.dns.records[0]} />

            <div className={`cd-wait${paused ? ' cd-wait--paused' : ''}`}>
                <span className="cd-wait__icon">{paused ? <PauseOutlined /> : <ClockCircleOutlined />}</span>
                <div className="cd-wait__body">
                    <div className="cd-wait__title">{paused ? i.t('domain:paused.title') : i.t('domain:wait.title')}</div>
                    <div className="cd-wait__text">
                        {paused
                            ? i.t('domain:paused.text', { hours: controller.settings.wait_hours })
                            : i.t('domain:wait.text', { hours: controller.settings.wait_hours })}
                    </div>
                    {!paused && (
                        <div className="cd-wait__next">
                            {next === null || next === 0 ? i.t('domain:wait.checking') : i.t('domain:wait.next', { time: formatCountdown(next) })}
                        </div>
                    )}
                </div>
                <Button icon={<ReloadOutlined />} loading={checking} onClick={() => controller.check(domain.id)}>
                    {i.t('domain:wait.check')}
                </Button>
            </div>
        </div>
    )
})

// ---------- Остальные состояния ----------

const ReadyToIssuePanel = observer(({ domain }: { domain: DomainItem }) => {
    const busy = !!controller.issuing[domain.id]

    return (
        <div className="cd-go">
            <div className="cd-go__head">
                <span className="cd-go__icon"><CheckCircleFilled /></span>
                <div className="cd-go__body">
                    <div className="cd-go__title">{i.t('domain:dnsOk.title')}</div>
                    <div className="cd-go__text">{i.t('domain:dnsOk.text')}</div>
                </div>
                <Button type="primary" size="large" icon={<SafetyCertificateOutlined />} loading={busy} onClick={() => controller.issue(domain.id)}>
                    {i.t('domain:dnsOk.button')}
                </Button>
            </div>
            <div className="cd-go__note">{i.t('domain:dnsOk.note')}</div>
            <Collapse
                ghost
                className="cd-help"
                items={[{
                    key: 'records',
                    label: i.t('domain:dnsOk.records'),
                    children: <div className="cd-recs">{domain.dns.records.map((record, index) => <Record key={`${record.type}_${record.host}_${index}`} record={record} domain={domain} />)}</div>,
                }]}
            />
        </div>
    )
})

const IssuingPanel = ({ domain }: { domain: DomainItem }) => {
    const slow = !!domain.issuing_since && dayjs().diff(dayjs(domain.issuing_since), 'minute') >= 5

    return (
        <div className="cd-issuing">
            <div className="cd-issuing__head">
                <span className="cd-issuing__icon"><SafetyCertificateOutlined /></span>
                <div>
                    <div className="cd-issuing__title">{i.t('domain:issuing.title')}</div>
                    <div className="cd-issuing__text">{i.t('domain:issuing.text')}</div>
                </div>
            </div>
            <div className="cd-bar" role="progressbar" aria-busy="true"><span /></div>
            {slow && <div className="cd-note cd-note--warning"><ExclamationCircleFilled />{i.t('domain:issuing.slow')}</div>}
        </div>
    )
}

const ReadyPanel = ({ domain }: { domain: DomainItem }) => {
    const expiring = !!domain.cert && domain.cert.days_left <= 14
    // www есть только у корневого домена: сервер даёт для него необязательную запись www, у поддомена её нет
    const hasWww = domain.www_included || domain.dns.records.some(record => !record.required)

    return (
        <div className="cd-ready">
            <div className="cd-ready__head">
                <span className="cd-ready__icon"><CheckOutlined /></span>
                <div className="cd-ready__body">
                    <div className="cd-ready__title">{i.t('domain:ready.title', { url: domain.url })}</div>
                    <div className="cd-ready__text">{i.t('domain:ready.text')}</div>
                </div>
                <Button type="primary" icon={<GlobalOutlined />} href={domain.url} target="_blank" rel="noopener noreferrer">
                    {i.t('domain:ready.open')}
                </Button>
            </div>

            {domain.source === 'managed' && (
                <ul className="cd-facts">
                    {domain.cert && (
                        <li className={expiring ? 'cd-fact cd-fact--warn' : 'cd-fact'}>
                            <SafetyCertificateOutlined />
                            {expiring
                                ? i.t('domain:ready.certSoon', { days: Math.max(0, domain.cert.days_left) })
                                : i.t('domain:ready.cert', { date: formatDate(domain.cert.expires_at) })}
                            <span className="cd-fact__muted">{i.t('domain:ready.certAuto')}</span>
                        </li>
                    )}
                    {hasWww && (
                        <li className="cd-fact">
                            {domain.www_included ? <CheckCircleFilled /> : <CloseCircleFilled className="cd-fact__off" />}
                            {domain.www_included
                                ? i.t('domain:ready.www', { domain: domain.unicode })
                                : i.t('domain:ready.noWww', { domain: domain.unicode })}
                        </li>
                    )}
                </ul>
            )}
        </div>
    )
}

const ErrorPanel = observer(({ domain }: { domain: DomainItem }) => {
    const code = domain.error?.code ?? 'issue_failed'
    const busy = !!controller.issuing[domain.id]

    return (
        <div className="cd-fail">
            <div className="cd-fail__head">
                <span className="cd-fail__icon"><ExclamationCircleFilled /></span>
                <div className="cd-fail__body">
                    <div className="cd-fail__title">{i.t('domain:error.title')}</div>
                    <div className="cd-fail__text">{i.t(`domain:errors.${code}`, { defaultValue: i.t('domain:errors.issue_failed') })}</div>
                </div>
                <Button icon={<ReloadOutlined />} loading={busy} onClick={() => controller.issue(domain.id)}>
                    {i.t('domain:error.retry')}
                </Button>
            </div>
            {domain.error?.message && (
                <Collapse
                    ghost
                    className="cd-help"
                    items={[{ key: 'details', label: i.t('domain:error.details'), children: <pre className="cd-fail__pre">{domain.error.message}</pre> }]}
                />
            )}
        </div>
    )
})

// ---------- Карточка домена ----------

const StatusIcon = ({ domain }: { domain: DomainItem }) => {
    switch (domain.status) {
        case 'active': return <GlobalOutlined />
        case 'issuing': return <SafetyCertificateOutlined />
        case 'dns_ok': return <CheckCircleFilled />
        case 'error': return <ExclamationCircleFilled />
        default: return <ClockCircleOutlined />
    }
}

const DomainCard = observer(({ domain }: { domain: DomainItem }) => {
    const removing = domain.status === 'removing'
    const multi = controller.activeCount > 1
    const celebrate = controller.celebrate === domain.id

    return (
        <article className={`cd-card cd-card--${domain.status}${celebrate ? ' cd-card--celebrate' : ''}`}>
            <div className="cd-card__head">
                <span className="cd-card__icon"><StatusIcon domain={domain} /></span>
                <div className="cd-card__title">
                    <div className="cd-card__name">
                        <span className="cd-card__domain">{domain.unicode}</span>
                        {domain.is_primary && multi && (
                            <Tooltip title={i.t('domain:card.primaryHint')}>
                                <span className="cd-chip cd-chip--primary"><StarFilled />{i.t('domain:card.primary')}</span>
                            </Tooltip>
                        )}
                        {domain.source === 'legacy' && <span className="cd-chip">{i.t('domain:card.legacy')}</span>}
                    </div>
                    <div className={`cd-card__status cd-card__status--${domain.status}`}>
                        {i.t(`domain:status.${domain.status}`)}
                        {domain.unicode !== domain.domain && <span className="cd-card__puny">{domain.domain}</span>}
                    </div>
                </div>

                <div className="cd-card__actions">
                    {domain.status === 'active' && multi && !domain.is_primary && (
                        <Button size="small" icon={<StarOutlined />} onClick={() => controller.makePrimary(domain.id)}>
                            {i.t('domain:card.makePrimary')}
                        </Button>
                    )}
                    {!removing && (
                        <Popconfirm
                            title={i.t('domain:card.removeTitle')}
                            description={i.t('domain:card.removeText', { domain: domain.unicode })}
                            okText={i.t('domain:card.removeConfirm')}
                            cancelText={i.t('domain:card.cancel')}
                            okButtonProps={{ danger: true }}
                            placement="bottomRight"
                            onConfirm={() => controller.remove(domain.id)}
                        >
                            <Button size="small" danger type="text" icon={<DeleteOutlined />}>{i.t('domain:card.remove')}</Button>
                        </Popconfirm>
                    )}
                </div>
            </div>

            {domain.source === 'managed' && !removing && <Steps domain={domain} />}

            <div className="cd-card__body">
                {(domain.status === 'pending_dns' || domain.status === 'dns_timeout') && <DnsPanel domain={domain} />}
                {domain.status === 'dns_ok' && <ReadyToIssuePanel domain={domain} />}
                {domain.status === 'issuing' && <IssuingPanel domain={domain} />}
                {domain.status === 'active' && <ReadyPanel domain={domain} />}
                {domain.status === 'error' && <ErrorPanel domain={domain} />}
                {removing && (
                    <div className="cd-wait cd-wait--quiet">
                        <span className="cd-wait__icon"><LoadingOutlined /></span>
                        <div className="cd-wait__body"><div className="cd-wait__title">{i.t('domain:card.removing')}</div></div>
                    </div>
                )}
            </div>
        </article>
    )
})

// ---------- Страница ----------

export const DomainPage = observer(() => {
    // Роль 0 (администратор) в хранилище не записывается: пусто — администратор, остальные роли — числом
    const canManage = !storeLocal.get('role')

    useEffect(() => {
        if (!canManage) return

        controller.get().then(() => controller.startPolling())

        return () => controller.stopPolling()
    }, [canManage])

    return (
        <>
            <Titles
                title={i.t('domain:title')}
                breadcrumb={[
                    { title: i.t('def:settings'), href: '/settings' },
                    { title: i.t('domain:title') },
                ]}
            />

            {!canManage && <div className="cd-note cd-note--info"><ExclamationCircleFilled />{i.t('domain:adminOnly')}</div>}

            {canManage && controller.loaded && (
                <div className="cd">
                    <Hero />

                    {controller.items.length === 0 && <HowItWorks />}

                    {controller.items.length > 0 && (
                        <section className="cd-list">
                            <div className="cd-list__head">
                                <h3 className="cd-list__title">{i.t('domain:list.title')}</h3>
                                <span className="cd-list__count">{i.t('domain:list.count', { count: controller.items.length, max: controller.settings.max })}</span>
                            </div>
                            {controller.items.map(domain => <DomainCard key={domain.id} domain={domain} />)}
                        </section>
                    )}
                </div>
            )}
        </>
    )
})
